import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ScaleIcon from '@mui/icons-material/Scale';
import Co2Icon from '@mui/icons-material/Co2';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Bar } from 'react-chartjs-2';
import type { Chart } from 'chart.js';
import { useTheme } from 'contexts/ThemeContext';
import usePickup from 'hooks/pickups/usePickup';
import { api } from 'services/api.service';
import { Asset } from 'types/asset';
import { Pickup } from 'types/pickup';
import { formatDate } from 'utils/helpers';
import {
  assetsToSustainabilityInput,
  computeSustainabilityMetrics,
} from 'lib/sustainabilityReportMetrics';
import 'styles/sustainabilityReportPrint.css';

const PRIMARY = '#184863';
const DEFAULT_DOCUMENT_TITLE = 'Electronics Recycling';
const SECONDARY = '#3e765d';

function formatCollectionAddress(pickup: Pickup | undefined): string {
  const loc = pickup?.location;
  if (!loc) return '—';
  const parts = [
    loc.address,
    loc.address2,
    [loc.city, loc.state].filter(Boolean).join(', '),
    loc.zipcode,
  ].filter(Boolean);
  return parts.join(' ') || '—';
}

interface MetricCellProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  fontFamily?: string;
}

function MetricCell({ title, value, icon, fontFamily }: MetricCellProps) {
  return (
    <Box
      className='sustainability-report-metric-cell'
      sx={{
        textAlign: 'center',
        px: 1,
        py: 2,
      }}
    >
      <Box
        sx={{
          color: PRIMARY,
          mb: 1,
          display: 'flex',
          justifyContent: 'center',
          '& svg': { fontSize: 40 },
        }}
      >
        {icon}
      </Box>
      <Typography
        variant='caption'
        component='p'
        sx={{
          fontWeight: 700,
          letterSpacing: 0.5,
          color: 'text.secondary',
          textTransform: 'uppercase',
          fontSize: '0.65rem',
          lineHeight: 1.3,
          minHeight: 32,
          fontFamily,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant='h4'
        component='p'
        sx={{
          fontWeight: 700,
          color: PRIMARY,
          mt: 1,
          fontFamily,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function SustainabilityReport() {
  const { theme: customerTheme } = useTheme();
  const { pickup, isLoading: pickupLoading } = usePickup();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fontFamily =
    customerTheme.customer.fontFamily || 'Montserrat, sans-serif';
  const logoUrl = customerTheme.customer.logo;

  useEffect(() => {
    if (pickup?.id) {
      setIsLoadingAssets(true);
      setError(null);
      const params = new URLSearchParams();
      params.append('filter[]', `pickup_id,is,${pickup.id}`);
      params.append('page[size]', '1000');

      api
        .get<{
          assets: Asset[];
          meta: { totalPages: number; count: number };
        }>(`/assets?${params.toString()}`)
        .then((response) => {
          setAssets(response.data.assets);
          setIsLoadingAssets(false);
        })
        .catch((err) => {
          console.error('Error fetching assets:', err);
          setError('Failed to load assets');
          setIsLoadingAssets(false);
        });
    }
  }, [pickup?.id]);

  const metrics = useMemo(
    () => computeSustainabilityMetrics(assetsToSustainabilityInput(assets)),
    [assets]
  );

  const chartMax = useMemo(() => {
    const peak = Math.max(
      metrics.chart.reused,
      metrics.chart.recycled,
      metrics.chart.landfill,
      1
    );
    return Math.ceil(peak * 1.2);
  }, [metrics.chart]);

  const chartData = useMemo(
    () => ({
      labels: ['REUSED', 'RECYCLED', 'LANDFILL'],
      datasets: [
        {
          data: [
            metrics.chart.reused,
            metrics.chart.recycled,
            metrics.chart.landfill,
          ],
          backgroundColor: PRIMARY,
          borderRadius: 6,
          barThickness: 36,
          maxBarThickness: 40,
        },
      ],
    }),
    [metrics.chart]
  );

  const chartOptions = useMemo(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      layout: {
        padding: { top: 8, right: 48, bottom: 8, left: 4 },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: PRIMARY,
          titleColor: '#fff',
          bodyColor: '#fff',
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          max: chartMax,
          display: false,
          grid: { display: false },
        },
        y: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: '#ffffff',
            font: { weight: '700' as const, size: 13, family: fontFamily },
            padding: 8,
          },
        },
      },
    }),
    [fontFamily, chartMax]
  );

  const barValueLabelPlugin = useMemo(
    () => ({
      id: 'sustainabilityBarValues',
      afterDatasetsDraw(chart: Chart<'bar'>) {
        const { ctx } = chart;
        const meta = chart.getDatasetMeta(0);
        const values = chart.data.datasets[0]?.data ?? [];

        ctx.save();
        ctx.fillStyle = '#ffffff';
        ctx.font = `700 14px ${fontFamily}`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        meta.data.forEach((bar, index) => {
          const raw = values[index];
          const value = typeof raw === 'number' ? raw : 0;
          const element = bar as { x: number; y: number };
          ctx.fillText(value.toLocaleString(), element.x + 10, element.y);
        });
        ctx.restore();
      },
    }),
    [fontFamily]
  );

  useEffect(() => {
    document.body.classList.add('sustainability-print');

    const restoreTitle = () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
    };

    const onBeforePrint = () => {
      document.title = ' ';
    };

    const onAfterPrint = restoreTitle;

    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);

    return () => {
      document.body.classList.remove('sustainability-print');
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
      restoreTitle();
    };
  }, []);

  const handlePrintToPDF = () => {
    document.title = ' ';
    window.print();
    window.setTimeout(() => {
      document.title = DEFAULT_DOCUMENT_TITLE;
    }, 500);
  };

  if (pickupLoading || isLoadingAssets) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Typography sx={{ fontFamily }}>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Typography color='error' sx={{ fontFamily }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className='sustainability-report'
      sx={{
        minHeight: '100vh',
        position: 'relative',
        pl: '20px',
        background: `linear-gradient(135deg, rgba(62, 118, 93, 0.06) 0%, rgba(24, 72, 99, 0.04) 50%, #f8faf9 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 8,
          background: PRIMARY,
          zIndex: 2,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 8,
          top: 0,
          bottom: 0,
          width: 8,
          background: SECONDARY,
          zIndex: 2,
        },
      }}
    >
      <Box
        className='sustainability-report-inner'
        sx={{
          maxWidth: 800,
          mx: 'auto',
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 4 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Header meta + logo */}
        <Grid
          container
          justifyContent='space-between'
          alignItems='flex-start'
          className='sustainability-report-header'
          sx={{ mb: 4 }}
        >
          <Grid item xs={8}>
            <Typography
              variant='body2'
              sx={{ fontFamily, color: 'text.secondary', lineHeight: 1.8 }}
            >
              <strong>Company Name</strong>
              <br />
              {pickup?.customerName || '—'}
              <br />
              <strong>Collection Number</strong>
              <br />
              {pickup?.orderNumber ?? '—'}
              <br />
              <strong>Collection Date</strong>
              <br />
              {(pickup?.scheduledDate && formatDate(pickup.scheduledDate)) || '—'}
              <br />
              <strong>Collection Address</strong>
              <br />
              {formatCollectionAddress(pickup)}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
            {logoUrl && (
              <Box
                component='img'
                src={logoUrl}
                alt='Assettech'
                className='sustainability-report-logo'
                sx={{
                  height: { xs: 56, md: 72 },
                  width: 'auto',
                  maxWidth: '100%',
                }}
              />
            )}
          </Grid>
        </Grid>

        {/* Title ~20% from top — approximated with margin */}
        <Box
          className='sustainability-report-title'
          sx={{ textAlign: 'center', mb: { xs: 3, md: 4 }, mt: { xs: 1, md: 2 } }}
        >
          <Typography
            component='h1'
            sx={{
              fontFamily,
              fontSize: { xs: '1.35rem', md: '1.75rem' },
              fontWeight: 400,
              letterSpacing: 2,
              color: PRIMARY,
              textTransform: 'uppercase',
            }}
          >
            YOUR{' '}
            <Box component='span' sx={{ fontWeight: 800 }}>
              SUSTAINABILITY
            </Box>{' '}
            OVERVIEW
          </Typography>
        </Box>

        {/* 3×3 metrics */}
        <Grid
          container
          className='sustainability-report-metrics'
          sx={{
            mb: 4,
            border: `1px solid ${SECONDARY}33`,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'rgba(255,255,255,0.85)',
            boxShadow: '0 4px 24px rgba(24, 72, 99, 0.08)',
          }}
        >
          <Grid item xs={4} sx={{ borderRight: `1px solid ${SECONDARY}22` }}>
            <MetricCell
              title='Total Assets Processed'
              value={metrics.totalAssetsProcessed.toLocaleString()}
              icon={<DevicesOtherIcon />}
              fontFamily={fontFamily}
            />
          </Grid>
          <Grid item xs={4} sx={{ borderRight: `1px solid ${SECONDARY}22` }}>
            <MetricCell
              title='Devices Reused'
              value={metrics.devicesReused.toLocaleString()}
              icon={<AutorenewIcon />}
              fontFamily={fontFamily}
            />
          </Grid>
          <Grid item xs={4}>
            <MetricCell
              title='Devices Recycled'
              value={metrics.devicesRecycled.toLocaleString()}
              icon={<DeleteOutlineIcon />}
              fontFamily={fontFamily}
            />
          </Grid>
          <Grid
            item
            xs={4}
            sx={{
              borderTop: `1px solid ${SECONDARY}22`,
              borderRight: `1px solid ${SECONDARY}22`,
            }}
          >
            <MetricCell
              title='Total Weight (kg)'
              value={metrics.totalWeightKg.toLocaleString()}
              icon={<ScaleIcon />}
              fontFamily={fontFamily}
            />
          </Grid>
          <Grid
            item
            xs={4}
            sx={{
              borderTop: `1px solid ${SECONDARY}22`,
              borderRight: `1px solid ${SECONDARY}22`,
            }}
          >
            <MetricCell
              title='Carbon Saved (tonnes)'
              value={metrics.carbonSavedTonnes.toFixed(2)}
              icon={<Co2Icon />}
              fontFamily={fontFamily}
            />
          </Grid>
          <Grid item xs={4} sx={{ borderTop: `1px solid ${SECONDARY}22` }}>
            <MetricCell
              title='Data Sanitized'
              value={metrics.dataSanitized.toLocaleString()}
              icon={<SecurityIcon />}
              fontFamily={fontFamily}
            />
          </Grid>
        </Grid>

        {/* Bar chart — horizontal bars, left to right */}
        <Box
          className='sustainability-report-chart'
          sx={{
            bgcolor: SECONDARY,
            borderRadius: 2,
            px: 2,
            py: 2,
            mb: 4,
            minHeight: 260,
            height: 260,
            position: 'relative',
            overflow: 'visible',
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
          }}
        >
          <Box
            className='sustainability-report-chart-canvas'
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              right: 12,
              bottom: 12,
            }}
          >
            <Bar
              data={chartData}
              options={chartOptions}
              plugins={[barValueLabelPlugin]}
            />
          </Box>
        </Box>

        {/* Compliance footer */}
        <Box className='sustainability-report-footer' sx={{ mb: 3 }}>
          <Typography
            variant='h6'
            sx={{
              fontFamily,
              fontWeight: 700,
              color: PRIMARY,
              mb: 1.5,
            }}
          >
            Compliance & Security
          </Typography>
          <List dense disablePadding>
            {[
              'All data-bearing devices securely sanitised',
              'Processing completed in line with GDPR and WEEE regulations',
              'Certified downstream recycling partners utilised',
              'Audit trail available via CAB documentation',
            ].map((text) => (
              <ListItem key={text} disableGutters sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 32, color: SECONDARY }}>
                  <CheckCircleOutlineIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontFamily,
                    color: 'text.secondary',
                  }}
                />
              </ListItem>
            ))}
          </List>
          <Typography
            variant='body2'
            sx={{
              fontFamily,
              color: 'text.secondary',
              mt: 2,
              fontStyle: 'italic',
              lineHeight: 1.6,
            }}
          >
            Supporting documentation including CAB reports, data destruction
            certificates and recycling evidence are provided on completion of
            each Job.
          </Typography>
        </Box>

        <Box className='print-hide' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='contained'
            onClick={handlePrintToPDF}
            sx={{
              bgcolor: PRIMARY,
              fontFamily,
              fontWeight: 700,
              px: 4,
              py: 1.5,
              '&:hover': { bgcolor: PRIMARY, opacity: 0.9 },
            }}
          >
            Print to PDF
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
